import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { SkeletonStatsGrid, Skeleton } from '../../components/ui/Skeleton';

interface SystemStats {
  users: {
    total: number;
    active: number;
    suspended: number;
    pending: number;
    superadmins: number;
    admins: number;
    newThisWeek: number;
  };
  forms: {
    total: number;
    published: number;
    draft: number;
    newThisWeek: number;
  };
  submissions: {
    total: number;
    new: number;
    today: number;
    thisWeek: number;
  };
  groups: {
    total: number;
    totalMemberships: number;
  };
  storage: {
    totalFiles: number;
    totalBytes: number;
    totalMB: number;
  };
  apiKeys: {
    total: number;
    active: number;
  };
  invitations: {
    pending: number;
  };
  recentActivity: Array<{
    id: string;
    action: string;
    resourceType: string;
    userEmail: string | null;
    createdAt: string;
  }>;
}

export default function SuperadminDashboardPage() {
  const { t } = useTranslation('superadmin');
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/stats');
      if (response.ok) {
        const data = await response.json() as SystemStats;
        setStats(data);
      } else {
        const data = await response.json() as { error?: string };
        setError(data.error || 'Failed to load statistics');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-5 w-16" />
        </div>

        {/* Main Stats Grid */}
        <SkeletonStatsGrid count={4} />

        {/* Secondary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow p-6">
              <Skeleton className="h-4 w-20 mb-2" />
              <Skeleton className="h-8 w-12 mb-1" />
              <Skeleton className="h-4 w-32" />
            </div>
          ))}
        </div>

        {/* User Breakdown */}
        <div className="bg-white rounded-lg shadow p-6">
          <Skeleton className="h-6 w-36 mb-4" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="text-center p-4 bg-gray-50 rounded-lg">
                <Skeleton className="h-8 w-12 mx-auto mb-2" />
                <Skeleton className="h-4 w-20 mx-auto" />
              </div>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-4 w-16" />
          </div>
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                <div>
                  <Skeleton className="h-4 w-48 mb-1" />
                  <Skeleton className="h-3 w-32" />
                </div>
                <Skeleton className="h-3 w-24" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">{t('dashboard.title')}</h1>
        <button
          onClick={loadStats}
          className="text-sm text-blue-600 hover:text-blue-800"
        >
          {t('dashboard.refresh')}
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Users Card */}
        <Link
          to="/superadmin/users"
          className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">{t('dashboard.totalUsers')}</p>
              <p className="text-3xl font-bold text-gray-900">{stats.users.total}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            <span className="text-green-600">{stats.users.active} {t('dashboard.active')}</span>
            {stats.users.newThisWeek > 0 && (
              <span className="ltr:ml-2 rtl:mr-2">+{stats.users.newThisWeek} {t('dashboard.thisWeek')}</span>
            )}
          </div>
        </Link>

        {/* Forms Card */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">{t('dashboard.totalForms')}</p>
              <p className="text-3xl font-bold text-gray-900">{stats.forms.total}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            <span className="text-green-600">{stats.forms.published} {t('dashboard.published')}</span>
            <span className="ltr:ml-2 rtl:mr-2">{stats.forms.draft} {t('dashboard.drafts')}</span>
          </div>
        </div>

        {/* Submissions Card */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">{t('dashboard.submissions')}</p>
              <p className="text-3xl font-bold text-gray-900">{stats.submissions.total}</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-full">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            <span className="text-blue-600">{stats.submissions.today} {t('dashboard.today')}</span>
            <span className="ltr:ml-2 rtl:mr-2">{stats.submissions.thisWeek} {t('dashboard.thisWeek')}</span>
          </div>
        </div>

        {/* Storage Card */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">{t('dashboard.storageUsed')}</p>
              <p className="text-3xl font-bold text-gray-900">{stats.storage.totalMB} MB</p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-full">
              <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
              </svg>
            </div>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            {stats.storage.totalFiles} {t('dashboard.filesUploaded')}
          </div>
        </div>
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-sm font-medium text-gray-500 mb-2">{t('dashboard.groups')}</h3>
          <p className="text-2xl font-bold text-gray-900">{stats.groups.total}</p>
          <p className="text-sm text-gray-500">{stats.groups.totalMemberships} {t('dashboard.totalMemberships')}</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-sm font-medium text-gray-500 mb-2">{t('dashboard.apiKeys')}</h3>
          <p className="text-2xl font-bold text-gray-900">{stats.apiKeys.active}</p>
          <p className="text-sm text-gray-500">{stats.apiKeys.total} {t('dashboard.total')} ({stats.apiKeys.total - stats.apiKeys.active} {t('dashboard.revoked')})</p>
        </div>

        <Link
          to="/superadmin/invitations"
          className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow"
        >
          <h3 className="text-sm font-medium text-gray-500 mb-2">{t('dashboard.pendingInvitations')}</h3>
          <p className="text-2xl font-bold text-gray-900">{stats.invitations.pending}</p>
          <p className="text-sm text-gray-500">{t('dashboard.awaitingAcceptance')}</p>
        </Link>
      </div>

      {/* User Breakdown */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{t('dashboard.userBreakdown')}</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-2xl font-bold text-gray-900">{stats.users.superadmins}</p>
            <p className="text-sm text-gray-500">{t('dashboard.superadmins')}</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-2xl font-bold text-gray-900">{stats.users.admins}</p>
            <p className="text-sm text-gray-500">{t('dashboard.admins')}</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-2xl font-bold text-green-600">{stats.users.active}</p>
            <p className="text-sm text-gray-500">{t('dashboard.active')}</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-2xl font-bold text-red-600">{stats.users.suspended}</p>
            <p className="text-sm text-gray-500">{t('dashboard.suspended')}</p>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">{t('dashboard.recentActivity')}</h3>
          <Link
            to="/superadmin/audit-logs"
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            {t('dashboard.viewAll')}
          </Link>
        </div>
        <div className="space-y-3">
          {stats.recentActivity.length === 0 ? (
            <p className="text-sm text-gray-500">{t('dashboard.noRecentActivity')}</p>
          ) : (
            stats.recentActivity.map((activity) => (
              <div
                key={activity.id}
                className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
              >
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {activity.action}
                  </p>
                  <p className="text-xs text-gray-500">
                    {activity.userEmail || t('dashboard.system')} · {activity.resourceType}
                  </p>
                </div>
                <p className="text-xs text-gray-400">
                  {new Date(activity.createdAt).toLocaleString()}
                </p>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
